import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'

import s from './MyCreatives.module.scss'

import CreativeCard from './components/CreativeCard'

import {
  CREATIVE_ICON_BY_VALUE,
  CREATIVE_TYPES_OPTIONS,
  CREATIVE_TYPE_BY_CREATIVE_VALUE,
} from 'constants/creativeTypeFilter'
import { SORT_ICON_BY_VALUE, SORT_OPTIONS } from 'constants/sortOptions'

import { usePagination } from 'hooks/usePagination'

import { creativesApi } from 'api/creatives-api'

import { PlayIcon } from 'assets/icons/PlayIcon'
import { SelectIcon } from 'assets/icons/SelectIcon'
import NoCreativesIcon from 'assets/svg/no-creatives.svg'

import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { BurgerWrapper } from 'components/UI/BurgerWrapper/BurgerWrapper'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import Header from 'components/UI/Header'
import { NoContent } from 'components/UI/NoContent/NoContent'
import PageTitle from 'components/UI/PageTitle'
import Search from 'components/UI/Search'

const MyCreativesPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { control, setValue } = useForm({
    mode: 'onBlur',
    defaultValues: {
      sortBy: SORT_OPTIONS[0],
      filterBy: CREATIVE_TYPES_OPTIONS[0],
    },
  })

  const {
    data: creatives,
    loading,
    totalElements,
    paging,
    setPaging,
    forceUpdate,
    handleFilterChange,
    handleDelete,
    deleteLoading,
  } = usePagination({
    getData: creativesApi.paging,
    formValueSetter: setValue,
    deleteData: creativesApi.delete,
  })

  //sets select value if query not empty on page open
  const sortBy = searchParams.get('d__sort') || ''
  useEffect(() => {
    if (sortBy)
      setValue(
        'sortBy', //@ts-ignore
        SORT_OPTIONS.find((opt) => opt.value === sortBy)
      )
  }, [])

  const filterBy = searchParams.get('f__creative_type') || ''
  useEffect(() => {
    if (filterBy) setValue('filterBy', { value: filterBy, label: (CREATIVE_TYPE_BY_CREATIVE_VALUE as any)[filterBy] })
  }, [])

  // after creation of new creative
  const creativeId = localStorage.getItem('creativeId') || 0

  useEffect(() => {
    if (creativeId) {
      navigate(`${creativeId}?${searchParams}`)
      localStorage.removeItem('creativeId')
      setPaging((prev) => ({ ...prev, page: { ...prev.page, page_number: 0 } }))
      forceUpdate()
    }
  }, [creativeId])

  //refresh after edit
  const isAfterEdit = localStorage.getItem('creativeEdit') || ''
  useEffect(() => {
    if (isAfterEdit) {
      setPaging((prev) => ({ ...prev, page: { ...prev.page, page_number: 0 } }))
      forceUpdate()
      localStorage.removeItem('creativeEdit')
    }
  }, [isAfterEdit])

  const title =
    paging?.quick_search?.length || paging?.filters?.length
      ? creatives?.length
        ? 'Found Creatives'
        : 'Nothing found'
      : creatives?.length
      ? 'My Creatives'
      : null

  return (
    <div data-testid="creatives-page">
      <Outlet />
      <Header>
        <div className={s.headerWrapper}>
          <div className={s.headerButtonsWrapper}>
            <CustomButton variant="secondary" onClick={() => navigate(`create?${searchParams}`)} size="180" className={s.headerButton}>
              Add Creative
            </CustomButton>
            <CustomButton variant="secondary" size="180" onClick={() => navigate(`create-stream?${searchParams}`)} className={s.headerButton}>
              Add Stream
            </CustomButton>
          </div>
          <BurgerWrapper breakpoint={848}>
            <SelectControlled
              icon={<SelectIcon />}
              name="sortBy"
              control={control}
              placeholder="Sort by..."
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'sortBy',
                  searchParamName: 'd__sort',
                  option: opt,
                })
              }
              options={SORT_OPTIONS}
              optionsIcons={SORT_ICON_BY_VALUE}
              className={s.sortSelect}
            />

            <SelectControlled
              icon={<PlayIcon />}
              name="filterBy"
              control={control}
              placeholder={CREATIVE_TYPES_OPTIONS[0].label}
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'filterBy',
                  searchParamName: 'f__creative_type',
                  option: opt,
                })
              }
              options={CREATIVE_TYPES_OPTIONS}
              optionsIcons={CREATIVE_ICON_BY_VALUE}
              className={s.sortSelect}
            />
            <Search paramPrefix="creatives" className={s.search} />
          </BurgerWrapper>
        </div>
        <PageTitle title={title} count={totalElements} color="secondary" loading={loading || deleteLoading} />
      </Header>
      <main>
      {creatives?.length ? (
        <div className={s.cardsWrapper} data-testid="creatives-page-cards-board">
          {creatives.map((creative) => (
            <CreativeCard key={creative.id} item={creative} deleteCreative={handleDelete} />
          ))}
        </div>
      ) : !paging?.quick_search?.length && !paging?.filters?.length && !loading ? (
        <NoContent icon={NoCreativesIcon} title="No Creatives" subtitle="You haven’t added any creatives yet." />
      ) : null}
      </main>
    </div>
  )
}

export default MyCreativesPage
